import {all, takeLatest} from '@redux-saga/core/effects';
import {AsyncResult, Loaded, Loading, LoadingAdditional} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {ImagesDto, ImageSearchResponseDto, PolicyDto} from 'Agent/Policies/api';
import policyApi from 'Agent/Policies/api/policyApi';
import {
  changePendingUnderwritingRequirements,
  changePolicies,
  changePolicy,
  changePolicyImages,
  fetchPendingUnderwritingRequirements,
  fetchPolicies,
  fetchPolicy,
  fetchPolicyImages,
  PolicyActionTypes,
} from 'Agent/Policies/ducks/policy';
import {call, put} from 'redux-saga/effects';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getApiCallGenerator} from 'Shared/utilities/sagaApiGenerators';

export type PolicyImages = ImageSearchResponseDto;
export type PolicyImage = ImagesDto;

export function* getPoliciesFunction(action: ReturnType<typeof fetchPolicies>) {
  const {sort, pageOption, isNewYork, encryptedIdentifier, filter, agentNumbers} = action.payload;
  if (pageOption) {
    yield put(changePolicies(new LoadingAdditional(pageOption)));
  } else {
    yield put(changePolicies(new Loading(pageOption)));
  }

  const result: AsyncResult<Page<PolicyDto>> = yield pageOption
    ? call(mergeWithNextPage, {existingPage: pageOption})
    : call(policyApi.searchPolicies, agentNumbers.map(prop('value')), isNewYork, encryptedIdentifier, filter, sort);

  yield pipe(
    result,
    E.map(value => new Loaded(value)),
    E.fold(
      value => put(changePolicies(value)),
      value => put(changePolicies(value))
    )
  );
}

export function* fetchPoliciesSaga() {
  yield takeLatest(PolicyActionTypes.FETCH_POLICIES, getPoliciesFunction);
}

export function* fetchPolicyImagesApi(action: ReturnType<typeof fetchPolicyImages>) {
  yield getApiCallGenerator(
    () => policyApi.getImages(action.payload.encryptedIdentifier, action.payload.policyId),
    changePolicyImages
  )(action);
}

export function* fetchPolicyImagesSaga() {
  yield takeLatest(PolicyActionTypes.FETCH_POLICY_IMAGES, fetchPolicyImagesApi);
}

const getPendingUnderwritingRequirementsFunction = (action: ReturnType<typeof fetchPendingUnderwritingRequirements>) =>
  policyApi.getPendingUnderwritingRequirements(
    action.payload.encryptedIdentifier,
    action.payload.agentNumbers,
    action.payload.isNewYork
  );

export function* fetchPendingUnderwritingRequirementsSaga() {
  yield takeLatest(
    PolicyActionTypes.FETCH_PENDING_UNDERWRITING_REQUIREMENTS,
    getApiCallGenerator(getPendingUnderwritingRequirementsFunction, changePendingUnderwritingRequirements)
  );
}

export const getPolicyFunction = (action: ReturnType<typeof fetchPolicy>) => {
  const {policyId, encryptedIdentifier, policyNumber} = action.payload;
  return policyApi.getPolicy(encryptedIdentifier, policyId, policyNumber);
};

export function* fetchPolicySaga() {
  yield takeLatest(PolicyActionTypes.FETCH_POLICY, getApiCallGenerator(getPolicyFunction, changePolicy));
}

export default function*() {
  yield all([
    fetchPoliciesSaga(),
    fetchPolicySaga(),
    fetchPolicyImagesSaga(),
    fetchPendingUnderwritingRequirementsSaga(),
  ]);
}
